<template>
  <div>
    <div class="row">
      <div class="col-12 mt-3">
        <Card body-classes="p-3" card-classes="shadow-sm">
          <template #header>
            <div class="d-flex flex-row align-items-center justify-content-between">
              <h5 class="mb-0 text-primary">
                <vue-feather type="calendar" class="me-2"></vue-feather>
                Daily Sales Summary
              </h5>
            </div>
          </template>
          <div>
            <div v-if="loading" class="text-center py-5">
              <Spinner spinner-class="mb-1" />
              <p class="text-muted mt-2">Loading daily sales data...</p>
            </div>
            <div v-else>
              <!-- Filters -->
              <div class="card border-0 shadow-sm mb-4">
                <div class="card-body">
                  <h6 class="text-muted mb-3">Filter Options</h6>
                  <div class="row g-3">
                    <div class="col-md-4">
                      <label for="from" class="form-label">From Date</label>
                      <div class="input-group">
                        <span class="input-group-text bg-light">
                          <vue-feather type="calendar" size="16"></vue-feather>
                        </span>
                        <input type="date" class="form-control" id="from" v-model="summary.from">
                      </div>
                    </div>
                    <div class="col-md-4">
                      <label for="to" class="form-label">To Date</label>
                      <div class="input-group">
                        <span class="input-group-text bg-light">
                          <vue-feather type="calendar" size="16"></vue-feather>
                        </span>
                        <input type="date" class="form-control" id="to" v-model="summary.to">
                      </div>
                    </div>
                    <div class="col-md-4">
                      <label for="user" class="form-label">User</label>
                      <div class="input-group">
                        <span class="input-group-text bg-light">
                          <vue-feather type="user" size="16"></vue-feather>
                        </span>
                        <select id="user" class="form-select" v-model="summary.user_id">
                          <option value="" selected>All users</option>
                          <option v-for="user in users" :key="user.id" :value="user.id">
                            {{ user.first_name }} {{ user.last_name }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Summary Cards -->
              <div class="row g-3 mb-4">
                <div class="col-lg-3 col-md-6 col-sm-12" v-for="(item, index) in infoCols" :key="index">
                  <div class="card h-100 border-0 shadow-sm">
                    <div class="card-body">
                      <div class="d-flex align-items-center">
                        <div class="rounded-circle p-3 me-3" :class="`bg-${item.bg}-light`">
                          <vue-feather :type="item.icon" :stroke="item.color" size="24"></vue-feather>
                        </div>
                        <div>
                          <h6 class="mb-0">{{ item.title }}</h6>
                          <small class="text-muted">{{ getDateRangeText() }}</small>
                        </div>
                      </div>
                      <div class="mt-3">
                        <h3 :class="`text-${item.bg}`">{{ item.value }}</h3>
                        <div class="progress mt-2" style="height: 5px;">
                          <div class="progress-bar" :class="`bg-${item.bg}`" role="progressbar"
                               :style="{width: getProgressWidth(index)}"
                               aria-valuenow="25" aria-valuemin="0" aria-valuemax="100"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Chart -->
              <div class="card border-0 shadow-sm">
                <div class="card-body">
                  <h6 class="text-muted mb-3">Sales Trend {{ getDateRangeText() }}</h6>
                  <LineChart
                      :data="this.analytics.data.datasets"
                      :labels="this.analytics.data.labels"
                  ></LineChart>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  </div>
</template>

<script>
import {decrypt} from "@/utils/crypto";
import analyticsService from "@/api/analyticsService";
import formatNumber from "@/utils/formatNumber";
import userService from "@/api/userService";
import notify from "@/utils/notify";
import LineChart from "@/components/charts/LineChart.vue";

export default {
  name: "DailyAnalytics",
  components: {LineChart},

  data(){
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    return {
      users: [],
      profile: {},
      store: {},
      loading: true,
      analytics: {
        data: {
          labels: [],
          datasets: []
        }
      },
      summary: {
        store_id: this.store.id,
        user_id: '',
        from: '',
        to: '',
      }
    }
  },

  methods: {
    async getAnalytics() {
      this.loading = true
      const usersResponse = await userService.getUsers()
      this.users = usersResponse?.data || []
      this.analytics = await analyticsService.searchStoreAnalytics(this.summary)
      this.analytics.data.datasets.forEach(e => {
        switch (e.label) {
          case 'Sales':
            e.backgroundColor = 'rgba(102, 16, 242, 0.2)'
            e.borderColor = 'darkviolet'
            break
          case 'Tax':
            e.backgroundColor = 'rgba(220, 53, 69, 0.2)'
            e.borderColor = 'crimson'
            break
          case 'Discount':
            e.backgroundColor = 'rgba(108, 117, 125, 0.2)'
            e.borderColor = 'gray'
            break
          case 'Profit':
            e.backgroundColor = 'rgba(40, 167, 69, 0.2)'
            e.borderColor = 'green'
            break
        }
      })
      this.loading = false
    },

    getProgressWidth(index) {
      // Calculate relative percentages for progress bars
      if (!this.analytics || !this.infoCols || this.infoCols.length === 0) {
        return '0%';
      }

      const values = this.infoCols.map(item => {
        const numValue = parseFloat(item.value.replace(/[^0-9.-]+/g, ''));
        return isNaN(numValue) ? 0 : numValue;
      });

      const maxValue = Math.max(...values);
      if (maxValue === 0) return '0%';

      const currentValue = values[index];
      return `${(currentValue / maxValue * 100).toFixed(0)}%`;
    },

    getDateRangeText() {
      if (!this.summary.from && !this.summary.to) {
        return 'All time';
      } else if (this.summary.from && !this.summary.to) {
        return `From ${this.formatDate(this.summary.from)}`;
      } else if (!this.summary.from && this.summary.to) {
        return `Until ${this.formatDate(this.summary.to)}`;
      } else {
        return `${this.formatDate(this.summary.from)} - ${this.formatDate(this.summary.to)}`;
      }
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  },

  computed: {
    infoCols() {
      return [
        {
          title:'Sales (' + this.analytics.count + ')',
          value:formatNumber(this.analytics.total - this.analytics.discount),
          icon:"shopping-cart",
          color:"darkviolet",
          bg:'primary',
        },
        {
          title:'Tax',
          value:formatNumber(this.analytics.tax),
          icon:"pie-chart",
          color:"crimson",
          bg:'secondary',
        },
        {
          title:'Discount',
          value:formatNumber(this.analytics.discount),
          icon:"percent",
          color:"gray",
          bg:'default',
        },
        {
          title:'Profit',
          value:formatNumber(this.analytics.profit),
          icon:"dollar-sign",
          color:"green",
          bg:'success',
        },
      ]
    },
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    await this.getAnalytics()
  },

  watch: {
    summary: {
      handler: async function () {
        let nowDiff = new Date(this.summary.from) - new Date()
        let nowDiffDays = nowDiff / (1000 * 60 * 60 * 24)

        const diffInMs = new Date(this.summary.to) - new Date(this.summary.from)
        const diffInDays = diffInMs / (1000 * 60 * 60 * 24)

        if (this.summary.from === '' && this.summary.to !== '') {
          notify.alert.info('Please select a start date')
          this.summary.to = ''
          return
        } else if (nowDiffDays > 0 || nowDiffDays < -366) {
          notify.alert.info('Please select a start date within the last 1 years')
          this.summary.from = ''
          return
        } else if (diffInDays > 366) {
          notify.alert.info('Please select a range within the last 1 years')
          this.summary.to = ''
          return
        }
        await this.getAnalytics()
      },
      deep: true
    }
  },

}
</script>

<style scoped>

</style>
